import { useEffect, useState, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, ArrowDownCircle, ArrowUpCircle, Paperclip, X, Trash2, FileText, ExternalLink, FileDown } from "lucide-react";

const CATEGORIES = ["kit", "medical", "food", "personal", "school", "transport", "other"];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_Q = Math.floor(new Date().getMonth() / 3) + 1;

/**
 * WardWallet modal — used by admin/staff on the ward card in AdminParents.
 *
 * Displays balance, transaction history and a form to add a credit (parent deposit)
 * or debit (expense) with an optional bill attachment (image / PDF).
 */
export default function WardWallet({ wardId, wardName, onClose }) {
  const [state, setState] = useState(null);
  const [type, setType] = useState("credit");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [method, setMethod] = useState("cash");
  const [txnRef, setTxnRef] = useState("");
  const [attach, setAttach] = useState(null); // {url, path, size}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [stmtYear, setStmtYear] = useState(CURRENT_YEAR);
  const [stmtQ, setStmtQ] = useState(CURRENT_Q);
  const [showStmt, setShowStmt] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { load(); }, [wardId]);
  const load = async () => {
    try { const { data } = await api.get(`/admin/wards/${wardId}/wallet`); setState(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttach(data);
      toast.success("Bill attached");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    if (!description.trim()) { toast.error("Add a short description"); return; }
    if (type === "credit" && method !== "cash" && !txnRef.trim()) {
      toast.error("Transaction reference is required for non-cash top-ups");
      return;
    }
    setSaving(true);
    try {
      const body = {
        type, amount: parseFloat(amount), description: description.trim(), category,
        attachment_url: attach?.url,
      };
      if (type === "credit") {
        body.method = method;
        body.txn_ref = txnRef.trim() || null;
      }
      await api.post(`/admin/wards/${wardId}/wallet/entry`, body);
      setAmount(""); setDescription(""); setAttach(null); setCategory("other");
      setTxnRef("");
      toast.success(type === "credit"
        ? `Top-up recorded via ${method.toUpperCase()} — posted to Bank Book`
        : "Amount deducted from wallet");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const url = `${BACKEND}/api/admin/wards/${wardId}/wallet/statement.pdf?year=${stmtYear}&quarter=${stmtQ}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = `wallet-${wardName.replace(/\s+/g, "_")}-Q${stmtQ}_${stmtYear}.pdf`;
      document.body.appendChild(a);
      a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(`Statement Q${stmtQ} ${stmtYear} downloaded`);
      setShowStmt(false);
    } catch (e) { toast.error(String(e?.message || e)); }
    finally { setDownloading(false); }
  };

  const voidEntry = async (txnId) => {
    if (!confirm("Void this transaction? Balance will be recomputed.")) return;
    try {
      await api.post(`/admin/wards/${wardId}/wallet/entry/${txnId}/void`, { reason: "reversed by admin" });
      toast.success("Voided");
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!state) return null;
  const bal = state.summary?.balance ?? 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0B1120] border border-white/12 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ward-wallet-modal">
        <div className="sticky top-0 bg-[#0B1120] border-b border-white/12 px-5 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className="brand-orange" size={22} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Ward Wallet</div>
              <div className="font-display text-lg font-black uppercase tracking-tight">{wardName}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowStmt((s) => !s)}
              data-testid="admin-wallet-statement-toggle"
              className="hidden sm:flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest border border-white/25 px-3 py-1.5 hover:bg-white/10"
            >
              <FileDown size={12} /> Statement
            </button>
            <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
          </div>
        </div>

        {showStmt && (
          <div className="mx-5 md:mx-6 mt-4 border border-brand-blue/40 bg-brand-blue/5 p-4" data-testid="admin-statement-picker">
            <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-2">// Quarterly wallet statement · PDF</div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={stmtYear} onChange={(e) => setStmtYear(Number(e.target.value))}
                data-testid="admin-statement-year"
                className="bg-transparent border border-white/20 py-2 px-3 text-sm focus:outline-none focus:border-brand-blue"
              >
                {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => (
                  <option key={y} value={y} className="bg-[#0B1120]">{y}</option>
                ))}
              </select>
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((q) => (
                  <button
                    key={q} onClick={() => setStmtQ(q)}
                    data-testid={`admin-statement-q${q}`}
                    className={`text-xs font-bold px-3 py-2 border ${stmtQ === q ? "border-brand-blue bg-brand-blue text-white" : "border-white/20 hover:bg-white/10"}`}
                  >Q{q}</button>
                ))}
              </div>
              <button
                onClick={downloadStatement} disabled={downloading}
                data-testid="admin-statement-download"
                className="ml-auto bg-brand-blue text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {downloading ? "Preparing…" : <><FileDown size={12} /> Download PDF</>}
              </button>
            </div>
          </div>
        )}

        {/* Balance card */}
        <div className={`m-5 md:m-6 p-6 border ${bal >= 0 ? "border-brand-orange/50 bg-brand-orange/10" : "border-red-500/50 bg-red-500/10"}`} data-testid="wallet-balance">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">Current Balance</div>
          <div className={`font-display text-4xl font-black ${bal >= 0 ? "brand-orange" : "text-red-400"}`}>
            ₹{Number(bal).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs font-mono text-white/60">
            <span>Total deposits: <span className="text-white">₹{Number(state.summary?.total_credits || 0).toLocaleString("en-IN")}</span></span>
            <span>Total spent: <span className="text-white">₹{Number(state.summary?.total_debits || 0).toLocaleString("en-IN")}</span></span>
          </div>
        </div>

        {/* Entry form */}
        <form onSubmit={submit} className="mx-5 md:mx-6 mb-6 bg-[#131A2B] border border-white/12 p-5" data-testid="wallet-form">
          <div className="flex gap-2 mb-4">
            <button type="button" onClick={() => setType("credit")} data-testid="wallet-type-credit"
              className={`flex-1 py-3 font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2 ${type === "credit" ? "bg-green-500/20 border border-green-500 text-green-300" : "border border-white/20 text-white/60"}`}>
              <ArrowDownCircle size={16} /> Credit (Deposit received)
            </button>
            <button type="button" onClick={() => setType("debit")} data-testid="wallet-type-debit"
              className={`flex-1 py-3 font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2 ${type === "debit" ? "bg-red-500/20 border border-red-500 text-red-300" : "border border-white/20 text-white/60"}`}>
              <ArrowUpCircle size={16} /> Debit (Expense paid)
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="wallet-amount" placeholder="Amount (₹)"
              className="bg-transparent border border-white/20 py-3 px-4 font-mono" />
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="wallet-category"
              className="bg-[#0B1120] border border-white/20 py-3 px-4 text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
            <input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="wallet-desc"
              placeholder="Short description (e.g. 'Cricket bat purchase')"
              className="md:col-span-2 bg-transparent border border-white/20 py-3 px-4 text-sm" />
          </div>

          {/* Desk-collection fields — required for credits so the top-up posts to Bank Book */}
          {type === "credit" && (
            <div className="mt-3 border border-brand-blue/30 bg-brand-blue/5 p-3">
              <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-2">// Desk collection · required</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="wallet-method"
                  className="bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm">
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank_transfer">Bank transfer</option>
                </select>
                <input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} data-testid="wallet-txn-ref"
                  placeholder={method === "cash" ? "Optional cash receipt no." : "Txn / UPI ref / cheque no. (required)"}
                  className="bg-transparent border border-white/20 py-2.5 px-3 text-sm font-mono" />
              </div>
              <div className="mt-2 text-[10px] text-white/50">Posts to Bank Book automatically. Wallet top-ups can also be paid online by the parent from their portal.</div>
            </div>
          )}

          {/* Bill attachment */}
          <div className="mt-3">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid="wallet-file-input" />
            {attach ? (
              <div className="flex items-center gap-2 bg-black/40 border border-white/12 px-3 py-2">
                <FileText size={16} className="brand-blue" />
                <a href={attach.url} target="_blank" rel="noreferrer" className="text-xs brand-blue underline truncate flex-1">Bill attached · view</a>
                <button type="button" onClick={() => setAttach(null)} className="text-white/50 hover:text-white"><X size={14} /></button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="wallet-attach-btn"
                className="w-full border border-dashed border-white/20 py-2.5 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white hover:border-white/40 flex items-center justify-center gap-2 disabled:opacity-50">
                <Paperclip size={13} /> {uploading ? "Uploading…" : "Attach Bill (image or PDF)"}
              </button>
            )}
          </div>

          <button type="submit" disabled={saving || uploading} data-testid="wallet-submit"
            className={`w-full mt-4 py-3 font-bold uppercase tracking-widest text-sm ${type === "credit" ? "bg-green-500 hover:bg-green-400" : "bg-red-500 hover:bg-red-400"} text-black disabled:opacity-50`}>
            {saving ? "Saving…" : type === "credit" ? "Add Credit" : "Deduct Amount"}
          </button>
        </form>

        {/* Transactions */}
        <div className="mx-5 md:mx-6 mb-8">
          <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-3">Transaction History</div>
          <div className="border border-white/12 divide-y divide-white/8" data-testid="wallet-txn-list">
            {state.transactions.length === 0 && (
              <div className="p-8 text-center text-white/40 text-sm">No transactions yet.</div>
            )}
            {state.transactions.map((t) => (
              <div key={t.id} className={`p-4 flex items-start gap-3 ${t.voided ? "opacity-50 line-through" : ""}`} data-testid={`wallet-txn-${t.id}`}>
                <div className={`h-9 w-9 flex items-center justify-center shrink-0 ${t.type === "credit" ? "bg-green-500/15" : "bg-red-500/15"}`}>
                  {t.type === "credit" ? <ArrowDownCircle size={16} className="text-green-400" /> : <ArrowUpCircle size={16} className="text-red-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="font-medium text-sm">{t.description}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{t.category}</div>
                  </div>
                  <div className="text-[11px] text-white/40 mt-1">
                    {new Date(t.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    {t.created_by && <span> · by {t.created_by}</span>}
                  </div>
                  {t.attachment_url && (
                    <a href={t.attachment_url} target="_blank" rel="noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-[11px] brand-blue hover:underline">
                      <Paperclip size={11} /> View bill <ExternalLink size={10} />
                    </a>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono font-bold ${t.type === "credit" ? "text-green-400" : "text-red-400"}`}>
                    {t.type === "credit" ? "+" : "−"}₹{Number(t.amount).toLocaleString("en-IN")}
                  </div>
                  {!t.voided && (
                    <button onClick={() => voidEntry(t.id)} data-testid={`wallet-void-${t.id}`}
                      className="mt-1 text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1">
                      <Trash2 size={11} /> Void
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
